import React, { useMemo } from 'react';
import { Plus, Minus, Lock } from 'lucide-react';
import { OrgFunction } from './FunctionTypes';
import NoDataFound from '../common/NoDataFound';
import ApprovalDot from '../common/ApprovalDot';

interface FunctionListProps {
    functions: OrgFunction[];
    loading: boolean;
    onEdit: (func: OrgFunction) => void;
    expandedIds: Set<number>;
    onToggleExpand: (id: number) => void;
    onWorkflow?: (func: OrgFunction) => void;
}

const FunctionList: React.FC<FunctionListProps> = ({
    functions, loading, onEdit, expandedIds, onToggleExpand, onWorkflow,
}) => {
    // Build the parent→child tree from the flat list. Root rows are those with
    // parentId 'root' (mapped from a null parent_function_id in dbToUi).
    const treeData = useMemo(() => {
        const build = (parentId: string): (OrgFunction & { children?: OrgFunction[] })[] =>
            functions
                .filter(f => (f.parentId || 'root') === parentId)
                .map(f => ({ ...f, children: build(f.id) }));
        return build('root');
    }, [functions]);

    const toggleExpand = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        onToggleExpand(id);
    };

    const FunctionRow = ({ node, level = 0 }: { node: OrgFunction & { children?: OrgFunction[] }; level?: number }) => {
        const hasChildren = node.children && node.children.length > 0;
        const numericId = Number(node.id);
        const isExpanded = expandedIds.has(numericId);
        const paddingLeft = level * 24 + 12;
        const active = Number((node as any).is_active) === 1;

        return (
            <React.Fragment>
                <div
                    onClick={() => onEdit(node)}
                    data-testid={`BTN_function_row_${node.id}_edit`}
                    className={`tree-list-row${level > 0 ? ' tree-list-row--child' : ''}${active ? '' : ' is-inactive'}`}
                >
                    <div
                        className="tree-list-cell tree-list-cell--dot"
                        onClick={(e) => { e.stopPropagation(); onWorkflow?.(node); }}
                        title="Workflow — view timeline & act"
                        data-testid={`BTN_function_row_${node.id}_workflow`}
                    >
                        <ApprovalDot
                            state={(node.workflow_state ?? 'draft') as any}
                            onClick={() => onWorkflow?.(node)}
                        />
                    </div>

                    <div className="tree-list-cell tree-list-cell--w-40">
                        {(node as any).code ? (
                            <span className="cell-code" data-testid={`CELL_function_code_${node.id}`}>
                                {(node as any).code}
                            </span>
                        ) : (
                            <span className="text-gray-400">—</span>
                        )}
                    </div>

                    <div
                        className="tree-list-cell tree-list-cell--main"
                        style={{ paddingLeft: `${paddingLeft}px` }}
                    >
                        <button
                            onClick={(e) => toggleExpand(e, numericId)}
                            data-testid={`BTN_function_row_${node.id}_toggle`}
                            className={`tree-list-toggle${hasChildren ? '' : ' tree-list-toggle--hidden'}`}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                            {isExpanded ? <Minus size={10} /> : <Plus size={10} />}
                        </button>

                        <div className="tree-list-row-title">
                            <span className="tree-list-row-name cell-name-lock">
                                {node.workflow_state === 'approved' && (
                                    <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                )}
                                {node.name}
                            </span>
                            {node.description && (
                                <span className="tree-list-row-sub">{node.description}</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className={`tree-list-children${isExpanded ? ' tree-list-children--open' : ''}`}>
                    <div className="tree-list-children-inner">
                        {hasChildren && node.children?.map(child => (
                            <FunctionRow key={child.id} node={child} level={level + 1} />
                        ))}
                    </div>
                </div>
            </React.Fragment>
        );
    };

    return (
        <div className="tree-list">
            <div className="tree-list-header">
                <div className="tree-list-header-cell tree-list-cell--dot" aria-label="Workflow status" />
                <div className="tree-list-header-cell tree-list-cell--w-40">Code</div>
                <div className="tree-list-header-cell tree-list-header-cell--main">Department Name</div>
            </div>

            <div className="tree-list-body">
                {loading ? (
                    <div className="tree-list-body-inner" aria-busy="true" aria-live="polite">
                        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                            <div key={i} className="tree-list-row tree-list-skeleton-row">
                                <div className="tree-list-cell tree-list-cell--dot">
                                    <div className="table-list-skeleton-bar skel-dot" />
                                </div>
                                <div className="tree-list-cell tree-list-cell--w-40">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--code" />
                                </div>
                                <div
                                    className="tree-list-cell tree-list-cell--main"
                                    style={{ paddingLeft: `${(i % 3 === 1 ? 36 : 12)}px` }}
                                >
                                    <div className="tree-list-skeleton-toggle" />
                                    <div className="tree-list-row-title">
                                        <div className="tree-list-skeleton-bar tree-list-skeleton-bar--title" />
                                        <div className="tree-list-skeleton-bar tree-list-skeleton-bar--sub" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : functions.length === 0 ? (
                    <NoDataFound
                        title="No Departments Found"
                        message="No departments have been defined yet. Create one to get started."
                    />
                ) : (
                    <div className="tree-list-body-inner">
                        {treeData.map(node => (
                            <FunctionRow key={node.id} node={node} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FunctionList;
